<template>
    <div>
        <div class="field is-horizontal">
            <div class="field-label is-normal">
                <label class="label">Search</label>
            </div>
            <div class="field-body">
                <div class="field">
                    <p class="control">
                        <input v-model.lazy="filter.id"
                               :disabled="loading"
                               class="input"
                               type="number"
                               :placeholder="$t('jackpots.id', 'ID')"
                               @change="loadJackpots();">
                    </p>
                </div>
                <div class="field-label is-normal">
                    <label class="label">Currency</label>
                </div>
                <div class="field">
                    <div class="control is-expanded">
                        <div class="select is-fullwidth">
                            <select v-model.lazy="filter.currency"
                                    :disabled="loading"
                                    @change="loadJackpots();">
                                <option :value="null"></option>
                                <option v-for="c in currencies"
                                        :key="c.id"
                                        :value="c.id">{{c.id}} {{c.symbol}} ({{c.description}})</option>
                            </select>
                        </div>
                    </div>
                </div>
                <div class="field-label is-normal">
                    <label class="label">State</label>
                </div>
                <div class="field">
                    <div class="control is-expanded">
                        <div class="select is-fullwidth">
                            <select v-model.lazy="filter.isCompleted"
                                    :disabled="loading"
                                    @change="loadJackpots();">
                                <option :value="null">All</option>
                                <option :value="true">Completed</option>
                                <option :value="false">Not completed</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <table class="table is-hoverable is-fullwidth is-bordered is-size-7">
            <thead>
                <tr>
                    <th rowspan="2">#</th>
                    <th rowspan="2">Type</th>
                    <th rowspan="2">Branch</th>
                    <th rowspan="2">Currency</th>
                    <th colspan="2">Trigger</th>
                    <th rowspan="2">Current Amount</th>
                    <th colspan="2">Limit Settings</th>
                    <th colspan="2">Charge Settings</th>
                    <th colspan="2"
                        rowspan="2">Progress</th>
                    <th rowspan="2">Started At</th>
                    <th rowspan="2">Ended At</th>
                    <th rowspan="2">Is Completed</th>
                    <th rowspan="2"></th>
                    <th rowspan="2"></th>
                </tr>
                <tr>
                    <th>Amount</th>
                    <th>Min Stake</th>
                    <th>Min</th>
                    <th>Max</th>
                    <th>Current Percent</th>
                    <th>Next Percent</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="jackpot of jackpots" :key="jackpot.number">
                    <td>
                        <router-link :to="{ name: 'branch-jackpot-edit', params: { branch: jackpot.branch, jackpotNumber: jackpot.number } }">{{ jackpot.number }}: {{ jackpot.name }}</router-link>
                    </td>
                    <td style="white-space:nowrap;">
                        <template v-if="jackpot.series.seriesLength == 1">
                            single jp
                        </template>
                        <template v-else>
                            #{{jackpot.series.inSeriesNumber}} of {{jackpot.series.seriesLength || '∞'}}
                        </template>
                        <br/>
                        {{(jackpot.sharedSettings && jackpot.sharedSettings.enabled) ? "shared" : "single win"}}
                    </td>
                    <td>
                        <router-link :to="{ name: 'branch-general', params: { branch: jackpot.branch } }">{{ jackpot.branch }}</router-link>
                    </td>
                    <td>{{ jackpot.currency }}</td>
                    <td>{{ jackpot.triggerSettings.amount | currency(jackpot.currency) }}</td>
                    <td>{{ jackpot.triggerSettings.stakeMin | currency(jackpot.currency) }}</td>
                    <td>{{ jackpot.state.amountCurrent | currency(jackpot.currency) }}</td>
                    <td>{{ jackpot.limitSettings.amountMin | currency(jackpot.currency) }}</td>
                    <td>{{ jackpot.limitSettings.amountMax | currency(jackpot.currency) }}</td>
                    <td>{{ jackpot.chargeSettings.stakePercentCurrent }}%</td>
                    <td>{{ jackpot.chargeSettings.stakePercentNext }}%</td>
                    <td style="min-width: 10em; padding-bottom: 0; vertical-align: middle; white-space: nowrap;">
                        <span :style="{ width: `${calcProgress(jackpot)}%` }"
                              class="jackpot-progress"></span><span v-if="calcProgress(jackpot) < 99.9"
                              :style="{ width: `${100 - calcProgress(jackpot)}%` }"
                              class="jackpot-progress-alt"></span>
                    </td>
                    <td>{{ calcProgress(jackpot) }}%</td>
                    <td>{{ jackpot.state.startedAtUtc | datetime }}</td>
                    <td>{{ jackpot.state.endedAtUtc | datetime }}</td>
                    <td>
                        <span :class="{'has-text-success has-text-weight-bold': jackpot.state.status == 'active', 'has-text-danger has-text-weight-bold': jackpot.state.status == 'unknown' || jackpot.state.status == 'inactive', 'has-text-grey has-text-weight-bold': jackpot.state.status == 'forbidden'}">
                            {{jackpot.state.status}}
                        </span>
                    </td>
                    <td style="white-space: nowrap; text-align: center;">
                        <a v-if="jackpot.state.status == 'active'"
                           class="button is-small is-warning"
                           @click="pause(jackpot)">Pause</a>
                        <a v-if="jackpot.state.status == 'inactive'"
                           class="button is-small is-success"
                           @click="resume(jackpot)">Resume</a>
                    </td>
                    <td style="white-space: nowrap; text-align: center;">
                        <a v-if="jackpot.state.status != 'completed' && jackpot.state.status != 'forbidden'"
                           class="button is-small is-danger"
                           @click="forbid(jackpot)">Forbid</a>
                    </td>
                </tr>
            </tbody>
        </table>
        <pagination @change="loadJackpots();"
                    ref="pagination" />
    </div>
</template>

<script>
import state from "../../state";

import jackpotService from "../../services/jackpot.service";
import notyService from "../../services/noty.service";

import pagination from "./pagination.vue";

export default {
    name: "jackpot-list",
    components: { pagination },
    props: {
        load: {
            type: Function,
            required: true
        }
    },
    data: () => ({
        jackpots: [],
        filter: {
            id: null,
            currency: null,
            isCompleted: false,
            sortBy: "num",
            sortOrder: "Descending"
        },
        loading: false
    }),
    computed: {
        clientNumber() {
            return Number(this.$route.params.clientNumber);
        },
        currencies: () => state.currencies.get()
    },
    async mounted() {
        await this.loadJackpots();
    },
    methods: {
        async loadJackpots() {
            this.loading = true;
            let filter = {
                id: this.filter.id,
                currency: this.filter.currency,
                isCompleted: this.filter.isCompleted,
                pageNumber: this.$refs.pagination.pageNumber,
                pageSize: this.$refs.pagination.pageSize,
                sortBy: this.filter.sortBy,
                sortOrder: this.filter.sortOrder
            };
            const data = await this.load(filter);
            this.jackpots.length = 0;
            this.jackpots.push(...data.items);
            this.$refs.pagination.set(data.pageNumber, data.totalPages);
            this.loading = false;
        },
        async pause(jackpot) {
            await jackpotService.pauseJackpot(jackpot);
            await this.loadJackpots();
            notyService.success(
                `Jackpot ${jackpot.number} paused successfully`
            );
        },
        async resume(jackpot) {
            await jackpotService.resumeJackpot(jackpot);
            await this.loadJackpots();
            notyService.success(
                `Jackpot ${jackpot.number} resumed successfully`
            );
        },
        async forbid(jackpot) {
            if (!window.confirm("Are you sure you want to forbid the jackpot?"))
                return;
            await jackpotService.forbidJackpot(jackpot);
            await this.loadJackpots();
            notyService.success(
                `Jackpot ${jackpot.number} forbidden successfully`
            );
        },
        calcProgress(jackpot) {
            return jackpotService.calcProgress(jackpot);
        }
    }
};
</script>

<style scoped>
.jackpot-progress {
    display: inline-block;
    max-width: 100%;
    height: 1em;
    margin: 0;
    background-color: green;
}
.jackpot-progress-alt {
    display: inline-block;
    max-width: 100%;
    height: 1em;
    margin: 0;
    border: 1px solid green;
}
</style>



// WEBPACK FOOTER //
// front/src/components/common/jackpot-list.vue